import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {MutableRefObject} from 'react';

import {CANVAS_RATIO} from '../Canvas';
import type {BaseObject, CanvasSize} from '../types';

type Position = {
  top: number;
  left: number;
};

type ObjectPlacement = {
  position: Position;
  scale: number;
};

interface UseObjectPlacementParams<T extends BaseObject> {
  object: T;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  canvasSize: CanvasSize;
  enabled?: boolean;
}

const DEFAULT_SCALE = 1;
const SMALL_NUMBER_THRESHOLD = 1e-6;
const MIN_VALID_CALCULATED_DIMENSION = 1e-3;
const FALLBACK_MIN_DIMENSION_VALUE = 1;
const POSITION_CHANGE_UPDATE_THRESHOLD = 1e-3;
const SCALE_CHANGE_UPDATE_THRESHOLD = 1e-6;
const ROTATION_ADJUSTMENT_DEGREES = 0.01;
const COS_2R_ZERO_THRESHOLD = 1e-5;

/**
 * Adjusts the rotation of a moveable object if its rotation angle (specifically Math.cos(2 * rotationRad))
 * is close to zero. This adjustment helps prevent division by zero or precision issues
 * in `useObjectPlacement` when calculating the object's original unscaled dimensions from its
 * measured rotated and scaled dimensions.
 * Objects should be normalized via this method before being passed to `useObjectPlacement`.
 */
export function normalizeMoveableObject<T extends BaseObject>({
  object,
  enabled = true,
}: {
  object: T;
  enabled?: boolean;
}) {
  if (!enabled) {
    return object;
  }

  const originalRotationDegrees = object.styles.rotate || 0;
  let displayRotation = originalRotationDegrees;
  const originalRotationRad = originalRotationDegrees * (Math.PI / 180);
  const cos2R = Math.cos(2 * originalRotationRad);

  if (Math.abs(cos2R) < COS_2R_ZERO_THRESHOLD) {
    displayRotation = originalRotationDegrees + ROTATION_ADJUSTMENT_DEGREES;
  }

  return {
    ...object,
    styles: {
      ...object.styles,
      rotate: displayRotation,
    },
  };
}

function adjustBoundaryPosition(
  candidatePos: number,
  candidateEnd: number,
  fittedDimension: number,
  canvasDimension: number
) {
  if (fittedDimension > canvasDimension) {
    return 0;
  } else if (candidatePos < 0) {
    return 0;
  } else if (candidateEnd > canvasDimension) {
    return canvasDimension - fittedDimension;
  }
  return candidatePos;
}

function validateDimension(dimension: number | undefined) {
  if (
    dimension === undefined ||
    dimension <= MIN_VALID_CALCULATED_DIMENSION ||
    isNaN(dimension)
  ) {
    return FALLBACK_MIN_DIMENSION_VALUE;
  }
  return dimension;
}

function calculateIntrinsicDimensions(
  measuredWidth: number,
  measuredHeight: number,
  scaleApplied: number,
  rotationRad: number
): [number, number] {
  if (scaleApplied < SMALL_NUMBER_THRESHOLD) {
    return [measuredWidth, measuredHeight];
  }

  const widthAtUnitScale = measuredWidth / scaleApplied;
  const heightAtUnitScale = measuredHeight / scaleApplied;
  const cosR_abs = Math.abs(Math.cos(rotationRad));
  const sinR_abs = Math.abs(Math.sin(rotationRad));
  const cos2R = Math.cos(2 * rotationRad);

  const intrinsicWidth =
    (widthAtUnitScale * cosR_abs - heightAtUnitScale * sinR_abs) / cos2R;
  const intrinsicHeight =
    (heightAtUnitScale * cosR_abs - widthAtUnitScale * sinR_abs) / cos2R;

  return [
    validateDimension(intrinsicWidth),
    validateDimension(intrinsicHeight),
  ];
}

function hasSignificantChange(
  newPosition: Position,
  newScale: number,
  currentPosition: Position,
  currentScale: number
) {
  return (
    Math.abs(newPosition.left - currentPosition.left) >
      POSITION_CHANGE_UPDATE_THRESHOLD ||
    Math.abs(newPosition.top - currentPosition.top) >
      POSITION_CHANGE_UPDATE_THRESHOLD ||
    Math.abs(newScale - currentScale) > SCALE_CHANGE_UPDATE_THRESHOLD
  );
}

/**
 * Determines whether the given object will overflow the given canvasSize.
 * If so, returns updated object coordinates that will prevent overflow.
 * Otherwise, returns the original object coordinates.
 *
 * Additionally returns a scale value to be used within a CSS transform -
 * if the object will overflow the canvasSize regardless of being repositioned
 * (i.e. in cases of large, unbroken text strings), the scale will be reduced
 * such that the object fits within the canvasSize.
 * Otherwise, the original object scale will be returned.
 */
export function useObjectPlacement<T extends BaseObject>({
  object,
  containerRef,
  canvasSize,
  enabled = true,
}: UseObjectPlacementParams<T>) {
  const placementHistory = useRef<{position: Position; scale: number}[]>([]);
  const {width: canvasWidth, height: canvasHeight} = canvasSize;

  const derivedObjectStyles = useMemo(() => {
    const cssX = (canvasWidth * (object.styles.x || 0)) / CANVAS_RATIO.width;
    const cssY = (canvasHeight * (object.styles.y || 0)) / CANVAS_RATIO.height;
    const scale = object.styles.scale || DEFAULT_SCALE;
    return {cssX, cssY, scale};
  }, [
    canvasWidth,
    canvasHeight,
    object.styles.x,
    object.styles.y,
    object.styles.scale,
  ]);

  const initialState: ObjectPlacement = useMemo(
    () => ({
      position: {top: derivedObjectStyles.cssY, left: derivedObjectStyles.cssX},
      scale: derivedObjectStyles.scale,
    }),
    [derivedObjectStyles]
  );

  const [objectPlacement, setObjectPlacement] =
    useState<ObjectPlacement>(initialState);

  const shouldResetState = useCallback(
    () =>
      objectPlacement.position.left !== initialState.position.left ||
      objectPlacement.position.top !== initialState.position.top ||
      objectPlacement.scale !== initialState.scale,
    [objectPlacement, initialState]
  );

  // eslint-disable-next-line complexity, max-statements
  useEffect(() => {
    if (!containerRef.current || !enabled || !canvasWidth || !canvasHeight) {
      if (shouldResetState()) {
        setObjectPlacement(initialState);
      }
      return;
    }

    const {width: measuredBoxWidth, height: measuredBoxHeight} =
      containerRef.current.getBoundingClientRect();

    if (measuredBoxWidth === 0 || measuredBoxHeight === 0) {
      if (shouldResetState()) {
        setObjectPlacement(initialState);
      }
      return;
    }

    const rotationRad = (object.styles.rotate || 0) * (Math.PI / 180);
    const [objectIntrinsicWidth, objectIntrinsicHeight] =
      calculateIntrinsicDimensions(
        measuredBoxWidth,
        measuredBoxHeight,
        objectPlacement.scale,
        rotationRad
      );

    const {
      cssX: targetCssX,
      cssY: targetCssY,
      scale: targetScale,
    } = derivedObjectStyles;
    const cosR = Math.cos(rotationRad);
    const sinR = Math.sin(rotationRad);

    // Calculate rotated bounding dimensions
    const rotatedBoundingWidth =
      Math.abs(objectIntrinsicWidth * cosR) +
      Math.abs(objectIntrinsicHeight * sinR);
    const rotatedBoundingHeight =
      Math.abs(objectIntrinsicHeight * cosR) +
      Math.abs(objectIntrinsicWidth * sinR);

    // Calculate fit scale
    let fitScale = DEFAULT_SCALE;
    if (
      rotatedBoundingWidth > SMALL_NUMBER_THRESHOLD &&
      rotatedBoundingHeight > SMALL_NUMBER_THRESHOLD
    ) {
      fitScale = Math.min(
        canvasWidth / rotatedBoundingWidth,
        canvasHeight / rotatedBoundingHeight
      );
    } else if (rotatedBoundingWidth > SMALL_NUMBER_THRESHOLD) {
      fitScale = canvasWidth / rotatedBoundingWidth;
    } else if (rotatedBoundingHeight > SMALL_NUMBER_THRESHOLD) {
      fitScale = canvasHeight / rotatedBoundingHeight;
    }

    const finalScale = Math.max(
      Math.min(targetScale, fitScale),
      MIN_VALID_CALCULATED_DIMENSION
    );

    // Calculate visual bounding box
    const fittedBoundingWidth = rotatedBoundingWidth * finalScale;
    const fittedBoundingHeight = rotatedBoundingHeight * finalScale;

    // Calculate corner offsets (transform-origin: top left)
    const w_scaled = objectIntrinsicWidth * finalScale;
    const h_scaled = objectIntrinsicHeight * finalScale;
    const corners: [number, number][] = [
      [0, 0], // p0
      [w_scaled * cosR, w_scaled * sinR], // p1
      [-h_scaled * sinR, h_scaled * cosR], // p2
      [w_scaled * cosR - h_scaled * sinR, w_scaled * sinR + h_scaled * cosR], // p3
    ];

    const offsetX = Math.min(...corners.map(([x]) => x));
    const offsetY = Math.min(...corners.map(([, y]) => y));

    // Calculate candidate positions
    const candidateVisualLeft = targetCssX + offsetX;
    const candidateVisualTop = targetCssY + offsetY;

    // Adjust boundaries
    const finalVisualLeft = adjustBoundaryPosition(
      candidateVisualLeft,
      candidateVisualLeft + fittedBoundingWidth,
      fittedBoundingWidth,
      canvasWidth
    );
    const finalVisualTop = adjustBoundaryPosition(
      candidateVisualTop,
      candidateVisualTop + fittedBoundingHeight,
      fittedBoundingHeight,
      canvasHeight
    );

    const finalPosition: Position = {
      left: finalVisualLeft - offsetX,
      top: finalVisualTop - offsetY,
    };

    // Oscillation detection
    const newPlacement = {position: finalPosition, scale: finalScale};
    placementHistory.current.push(newPlacement);
    if (placementHistory.current.length > 3) {
      placementHistory.current.shift();
    }

    const isOscillating = placementHistory.current
      .slice(0, -1)
      .some(
        (prev) =>
          !hasSignificantChange(
            newPlacement.position,
            newPlacement.scale,
            prev.position,
            prev.scale
          )
      );

    if (isOscillating) {
      return;
    }

    // Update state if there's significant change
    if (
      hasSignificantChange(
        finalPosition,
        finalScale,
        objectPlacement.position,
        objectPlacement.scale
      )
    ) {
      setObjectPlacement({position: finalPosition, scale: finalScale});
    }
  }, [
    containerRef,
    enabled,
    canvasWidth,
    canvasHeight,
    derivedObjectStyles,
    initialState,
    objectPlacement,
    object.styles.rotate,
    shouldResetState,
  ]);

  return objectPlacement;
}
